import { ComponentProps, PropsWithChildren } from "react";
import { ArrowUpRight } from "@tamagui/lucide-icons";
import { styled, Text, View, XStack } from "tamagui";

import { Card } from "../../ui/card";
import { Progress } from "../../ui/progress";

const ScoreText = styled(Text, {
  name: "ScoreText",
  fontFamily: "$subtitle",
  variants: {
    state: {
      success: {
        color: "$score-10-9",
      },
      warning: {
        color: "$warning-500",
      },
      neutral: {
        color: "$gray-600",
      },
      danger: {
        color: "$error-500",
      },
    },
    font: {
      md: {
        fontWeight: "$5",
        fontSize: "$7.3",
        lineHeight: "$7.3",
      },

      sm: {
        fontWeight: "$5",
        fontSize: "$6.8",
        lineHeight: "$6.8",
      },
    },
  } as const,
  defaultVariants: {
    state: "neutral",
    font: "md",
  },
});

const ScoreContainer = styled(Text, {
  name: "ScoreContainer",
  fontFamily: "$subtitle",
  color: "$gray-100",
  variants: {
    font: {
      md: {
        fontWeight: "$5",
        fontSize: "$7.3",
        lineHeight: "$7.3",
      },
      sm: {
        fontWeight: "$5",
        fontSize: "$6.8",
        lineHeight: "$6.8",
      },
    },
  } as const,
  defaultVariants: {
    font: "md",
  },
});

type ScoreState = ComponentProps<typeof ScoreText>["state"];

type ScoreFont = ComponentProps<typeof ScoreText>["font"];

const Score: React.FC<
  PropsWithChildren & { max?: number; state: ScoreState; font?: ScoreFont }
> = ({ state, max = 100, font = "md", children }) => {
  return (
    <ScoreContainer font={font}>
      <ScoreText state={state} font={font}>
        {children}
      </ScoreText>
      /{max}
    </ScoreContainer>
  );
};

type ContainerProps = ComponentProps<typeof Card>;

const Container: React.FC<ContainerProps> = (props) => {
  return <Card variant="default" shadow="md" gap={"$3"} {...props} />;
};

const Title = styled(Text, {
  name: "SliceCardTitle",
  fontFamily: "$subtitle",
  color: "$gray-primary-900",
  variants: {
    font: {
      lg: {
        fontWeight: "$6",
        fontSize: "$7",
        lineHeight: "$7",
      },

      md: {
        fontWeight: "$6",
        fontSize: "$5",
        lineHeight: "$5",
      },
    },
  } as const,
  defaultVariants: {
    font: "lg",
  },
});

const Description = styled(Text, {
  name: "Description",
  fontFamily: "$subtitle",
  color: "$gray-primary-900",
  variants: {
    font: {
      lg: {
        fontWeight: "$4",
        fontSize: "$3",
        lineHeight: "$3",
      },
    },
  } as const,
  defaultVariants: {
    font: "lg",
  },
});

const DynamicText = styled(Text, {
  name: "DynamicText",
  fontFamily: "$subtitle",
  numberOfLines: 1,
  ellipsizeMode: "tail",
  variants: {
    state: {
      success: {
        color: "$score-10-9",
      },
      warning: {
        color: "$warning-500",
      },
      neutral: {
        color: "$gray-600",
      },
      danger: {
        color: "$error-500",
      },
    },
    weight: {
      lg: {
        fontWeight: "$6",
      },
      md: {
        fontWeight: "$5",
      },
    },

    font: {
      lg: {
        fontSize: "$5",
        lineHeight: "$5",
      },
      md: {
        fontSize: "$3",
        lineHeight: "$3",
      },

      sm: {
        fontSize: "$2",
        lineHeight: "$2",
      },
    },
  } as const,
});

const Arrow = styled(ArrowUpRight, {
  name: "Arrow",
  variants: {
    size: {
      md: {
        height: "$2.5",
        width: "$2.5",
      },
    },

    state: {
      success: {
        color: "$score-10-9",
      },
      warning: {
        color: "$warning-500",
        rotate: "95deg",
      },
      neutral: {
        display: "none",
      },
      danger: {
        rotate: "95deg",
        color: "$error-500",
      },
    },
  } as const,
  defaultVariants: {
    size: "md",
  },
});

//TODO: should be moved to ui
const Separator = styled(View, {
  name: "Separator",
  backgroundColor: "$gray-200",
  variants: {
    variant: {
      horizontal: {
        height: "$0.1",
        width: "100%",
      },
      vertical: {
        height: "100$",
        width: "$0.1",
      },
    },
  } as const,
  defaultVariants: {
    variant: "horizontal",
  },
});

type DynamicsState = ComponentProps<typeof DynamicText>["state"];

const Dynamics: React.FC<{
  state: DynamicsState;
  label: string | null | undefined;
  description: string | null | undefined;
  labelState: DynamicsState;
  value: string | null | undefined;
}> = ({ state, label, value, labelState, description }) => {
  return (
    <View data-id={"slice-card-dynamics"} width={"100%"} flex={1}>
      <DynamicText
        data-id={"slice-card-dynamics-label"}
        font="md"
        weight="lg"
        state={labelState}
      >
        {label}
      </DynamicText>
      <XStack width={"100%"} flexShrink={1} alignItems="center" gap={"$0.5"}>
        <XStack alignItems="center">
          <Arrow state={state} />
          <DynamicText
            data-id={"slice-card-dynamics-value"}
            font="sm"
            weight="md"
            state={state}
          >
            {value}
          </DynamicText>
        </XStack>
        <DynamicText
          data-id={"slice-card-dynamics-description"}
          font="sm"
          weight="md"
          width={"100%"}
          state={"neutral"}
        >
          {description}
        </DynamicText>
      </XStack>
    </View>
  );
};

export const SliceCard = {
  Container,
  Score,
  Progress,
  Title,
  Description,
  Dynamics,
  Separator,
};
